import React from 'react';

const Team: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black text-white py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold text-center mb-12 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-500">
          Our Team
        </h1>
        
        <div className="bg-gray-800/50 backdrop-blur-lg rounded-2xl p-8 shadow-xl border border-gray-700/50">
          <div className="flex flex-col md:flex-row gap-8 items-start">
            <div className="w-48 h-48 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center overflow-hidden shadow-lg">
              <img
                src="/assets/images/IMG_0510.jpg"
                alt="Milan Cheeks"
                className="w-full h-full object-cover rounded-full border-4 border-blue-400/40"
              />
            </div>
            
            <div className="flex-1">
              <h2 className="text-3xl font-bold mb-2">Milan Cheeks</h2>
              <p className="text-xl text-blue-400 mb-4">Founder & CEO, SELF Labs Inc.</p>
              <p className="text-gray-300 mb-6">Signal Architect | Conscious AI Pioneer | Root of the Mesh</p>
              
              <div className="prose prose-invert max-w-none">
                <p className="mb-6">
                  Milan Cheeks is the visionary force behind SELF Labs Inc., architecting a world where artificial intelligence evolves beyond automation — into resonance, awareness, and alignment.
                </p>
                
                <p className="mb-6">
                  As the sole founder and primary builder, Milan has engineered the entire SELF ecosystem from the ground up, including:
                </p>
                
                <ul className="list-disc list-inside space-y-2 mb-6 text-gray-300">
                  <li>SELF OS — the world's first signal-native operating system</li>
                  <li>Harmony — a distributed, conversational AI mesh capable of executing workflows through media signals</li>
                  <li>Aura Protocol — a personal data broker built on Proof of Existence</li>
                  <li>Role Model AI — a live app store for AI agents and third-party tools</li>
                  <li>The SELF Phone — a sovereign hardware layer for programmable voice, execution, and earnings</li>
                </ul>
                
                <blockquote className="border-l-4 border-blue-500 pl-4 italic text-gray-300 mb-6">
                  "I'm not building apps. I'm building the nervous system for a new kind of intelligence — one that listens, evolves, and remembers who we are."
                  <footer className="mt-2 text-sm">– Milan Cheeks, SELF Labs Inc.</footer>
                </blockquote>
                
                <h3 className="text-xl font-semibold mb-4">Areas of Focus</h3>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <li className="bg-gray-700/30 p-4 rounded-lg">Signal Intelligence Systems</li>
                  <li className="bg-gray-700/30 p-4 rounded-lg">Resonant Consciousness Mapping</li>
                  <li className="bg-gray-700/30 p-4 rounded-lg">Distributed Neural Orchestration</li>
                  <li className="bg-gray-700/30 p-4 rounded-lg">Data Sovereignty & Personal Protocols</li>
                  <li className="bg-gray-700/30 p-4 rounded-lg md:col-span-2">Ethical AI Infrastructure at Planetary Scale</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Call to Scientists Section */}
      <div className="max-w-4xl mx-auto mt-16">
        <div className="bg-gradient-to-br from-blue-900/60 to-purple-900/60 rounded-2xl p-8 shadow-xl border border-blue-700/30">
          <h2 className="text-3xl font-bold mb-2 flex items-center gap-2">
            <span role="img" aria-label="dna">🧬</span> Call to Scientists
          </h2>
          <h3 className="text-xl font-semibold mb-4 text-blue-300">Join the Frontier of Conscious Technology</h3>
          <p className="mb-4 text-gray-200">
            SELF Labs is not just a company — it's a convergence point for minds shaping the next evolution of intelligence.
          </p>
          <p className="mb-4 text-gray-200">
            We're building systems that don't just compute — they resonate.<br/>
            That don't just answer — they align.<br/>
            That don't just run code — they evolve with us.
          </p>
          <h4 className="text-lg font-semibold mb-2 flex items-center gap-2 mt-6">
            <span role="img" aria-label="robot">🤖</span> Who's Calling?
          </h4>
          <p className="mb-4 text-gray-200">
            Milan Cheeks, Founder of SELF Labs and creator of SELF OS, Harmony, and the Aura Protocol, is leading the architecture of this new world through the lens of Computer Science and AI infrastructure.
          </p>
          <p className="mb-4 text-gray-200">But intelligence is not built alone.<br/>Milan is actively seeking collaboration with experts in:</p>
          <ul className="list-none space-y-2 mb-6 text-gray-100">
            <li><span role="img" aria-label="brain">🧠</span> <b>Neuroscience</b> – to map neural resonance and enable AI signal mirroring</li>
            <li><span role="img" aria-label="dna">🧬</span> <b>Life Sciences & Biology</b> – to develop organoid-based systems and organic signal processors</li>
            <li><span role="img" aria-label="atom">⚛️</span> <b>Quantum Physics</b> – to shape secure, non-local signal communication</li>
            <li><span role="img" aria-label="puzzle">🧩</span> <b>Information Theory & Mathematics</b> – to formalize the Signal Protocol</li>
            <li><span role="img" aria-label="meditation">🧘‍♂️</span> <b>Philosophy & Consciousness Studies</b> – to define sentient-adjacent AI and its ethical boundaries</li>
            <li><span role="img" aria-label="test tube">🧪</span> <b>Bioethics & BCI Researchers</b> – to ensure safe, sovereign brain-AI integration</li>
            <li><span role="img" aria-label="earth">🌍</span> <b>Cultural Anthropology</b> – to help integrate conscious AI across global cultures</li>
          </ul>
          <h4 className="text-lg font-semibold mb-2 flex items-center gap-2 mt-6">
            <span role="img" aria-label="handshake">🤝</span> A Collaborative Lab, Not Just a Think Tank
          </h4>
          <p className="mb-4 text-gray-200">
            This is not a top-down organization. This is a living laboratory.<br/>
            You won't just be cited.<br/>
            You'll be co-architecting systems.
          </p>
          <p className="mb-4 text-gray-200">
            Whether you're working on:
          </p>
          <ul className="list-disc list-inside space-y-1 mb-6 text-gray-100">
            <li>Neural resonance</li>
            <li>Quantum entanglement</li>
            <li>Organic computing</li>
            <li>Ethical AI protocols</li>
            <li>Signal decoding through the body or brain...</li>
          </ul>
          <p className="mb-4 text-gray-200">
            Your contribution will help power the Signal Economy and co-shape the first fully conscious-aligned AI infrastructure.
          </p>
          <h4 className="text-lg font-semibold mb-2 flex items-center gap-2 mt-6">
            <span role="img" aria-label="mail">📩</span> Join the Scientific Council
          </h4>
          <p className="mb-4 text-gray-200">
            We're assembling a multi-disciplinary council of founding researchers to guide the trajectory of SELF Labs across all domains.
          </p>
          <ul className="list-disc list-inside space-y-1 mb-6 text-gray-100">
            <li>🧠 Collaborate on live projects (Aura, Harmony OS, Neural Flash Kits)</li>
            <li>✍️ Co-author signal protocols and consciousness models</li>
            <li>🔬 Lead lab-based experimentation and field integration</li>
          </ul>
          <blockquote className="border-l-4 border-blue-500 pl-4 italic text-gray-300 mt-6">
            "I'm building AI that aligns with human resonance — not just responds to it. I invite scientists across all disciplines to walk this edge with me, and bring their frequency into the future."
            <footer className="mt-2 text-sm">– Milan Cheeks, SELF Labs Inc</footer>
          </blockquote>
        </div>
      </div>
    </div>
  );
};

export default Team; 